import React, { useState, useEffect } from 'react';
import PhoneNumberModal from '../components/PhoneNumberModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './PhoneNumbers.css';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPhoneNumbers,
  createPhoneNumber,
  updatePhoneNumberStatus,
  deletePhoneNumber,
  syncPhoneNumbers,
  phoneNumberRegister
} from '../store/slices/phoneNumbersDynamicSlice';
import { useNavigate } from 'react-router-dom';

const PhoneNumbers = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingPhoneNumber, setEditingPhoneNumber] = useState(null);
  const [phoneNumberToDelete, setPhoneNumberToDelete] = useState(null);
  const [selectedPhoneForRegistration, setSelectedPhoneForRegistration] = useState(null);
  const [registrationPin, setRegistrationPin] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { 
    items: phoneNumbers, 
    loading, 
    error, 
    syncing 
  } = useSelector(state => state.phoneNumbersDynamic);

  const getPhoneNumberId = (phone) => {
    if (!phone) return null;
    return phone.phoneNumberId || phone.phone_number_id || phone.id || phone._id || null;
  };

  useEffect(() => {
    dispatch(fetchPhoneNumbers({ 
      page: 1, 
      limit: 20 
    })).unwrap().catch(err => {
      console.error('Failed to fetch phone numbers:', err);
    });
  }, [dispatch]);

  const statuses = ['all', 'active', 'inactive', 'restricted'];
  const qualityRatings = ['all', 'GREEN', 'YELLOW', 'RED', 'UNKNOWN'];

  const filteredPhoneNumbers = phoneNumbers.filter(phone => {
    const matchesSearch = 
      phone.displayPhoneNumber?.includes(searchQuery) ||
      phone.phoneNumber?.includes(searchQuery) ||
      phone.businessAccountName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      phone.phoneNumberId?.includes(searchQuery);
    
    const matchesStatus = 
      selectedFilter === 'all' || phone.status === selectedFilter;

    return matchesSearch && matchesStatus;
  });

  const handleAddPhoneNumber = () => {
    setEditingPhoneNumber(null);
    setIsModalOpen(true);
  };

  const handleEditPhoneNumber = (phone) => {
    setEditingPhoneNumber(phone);
    setIsModalOpen(true);
  };

  const openRegisterModal = (phone) => {
    setSelectedPhoneForRegistration(phone);
    setRegistrationPin('');
    setIsRegisterModalOpen(true);
  };

  const handleConnectPhoneNumber = async () => {
    try {
      const phone = selectedPhoneForRegistration;
      const phoneNumberId = getPhoneNumberId(phone);
      const pin = registrationPin;

      if (!phoneNumberId) {
        throw new Error('Phone number ID is missing.');
      }

      if (!/^\d{6}$/.test(pin)) {
        throw new Error('Please enter a valid 6-digit registration PIN.');
      }

      await dispatch(phoneNumberRegister({ phoneNumberId, pin })).unwrap();
      setIsRegisterModalOpen(false);
      setSelectedPhoneForRegistration(null);
      setRegistrationPin('');
    } catch (err) {
      console.error('Failed to register phone number:', err);
      alert(err?.message || 'Unable to connect phone number.');
    }
  };

  const handleDeletePhoneNumber = (phone) => {
    setPhoneNumberToDelete(phone);
    setIsDeleteModalOpen(true);
  };

  const handleSavePhoneNumber = async (formData) => {
    try {
      if (editingPhoneNumber?._id) {
        // Update existing phone number
        await dispatch(updatePhoneNumberStatus({
          phoneNumberId: editingPhoneNumber._id,
          status: formData.status,
          isPrimary: formData.isPrimary
        })).unwrap();
      } else {
        // Create new phone number
        await dispatch(createPhoneNumber({
          phoneNumber: formData.phoneNumber,
          displayPhoneNumber: formData.displayPhoneNumber,
          phoneNumberId: formData.phoneNumberId,
          status: formData.status,
          isPrimary: formData.isPrimary,
          isDefault: formData.isDefault
        })).unwrap();
      }
      setIsModalOpen(false);
      setEditingPhoneNumber(null);
      // Refresh the list
      dispatch(fetchPhoneNumbers({ 
        page: 1, 
        limit: 20 
      }));
    } catch (err) {
      console.error('Failed to save phone number:', err);
      alert('Error saving phone number. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    if (phoneNumberToDelete?._id) {
      try {
        await dispatch(deletePhoneNumber(phoneNumberToDelete._id)).unwrap();
        setIsDeleteModalOpen(false);
        setPhoneNumberToDelete(null);
        // Refresh the list
        dispatch(fetchPhoneNumbers({
          page: 1, 
          limit: 20 
        }));
      } catch (err) {
        console.error('Failed to delete phone number:', err);
        alert('Error deleting phone number. Please try again.');
      }
    }
  };

  const handleSync = async () => {
    try {
      await dispatch(syncPhoneNumbers()).unwrap();
      // Refresh the list
      dispatch(fetchPhoneNumbers({ 
        page: 1, 
        limit: 20 
      }));
    } catch (err) {
      console.error('Failed to sync phone numbers:', err);
      alert('Error syncing phone numbers. Please try again.');
    }
  };

  const getQualityBadge = (rating) => {
    const config = {
      'GREEN': { color: '#10b981', bg: '#d1fae5', label: 'High Quality' },
      'YELLOW': { color: '#f59e0b', bg: '#fef3c7', label: 'Medium Quality' },
      'RED': { color: '#ef4444', bg: '#fee2e2', label: 'Low Quality' },
      'UNKNOWN': { color: '#6b7280', bg: '#f3f4f6', label: 'Not Rated' }
    };

    const quality = config[rating] || config['UNKNOWN'];
    return (
      <span 
        className="quality-badge" 
        style={{ background: quality.bg, color: quality.color }}
        title={quality.label}
      >
        {rating || 'UNKNOWN'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      'CONNECTED': { color: '#10b981', bg: '#d1fae5' },
      'INACTIVE': { color: '#ef4444', bg: '#fee2e2' },
      'RESTRICTED': { color: '#f59e0b', bg: '#fef3c7' }
    };

    const statusConfig = config[status] || config['INACTIVE'];
    return (
      <span 
        className="status-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status || 'unknown'}
      </span>
    );
  };

  const getVerificationStatusBadge = (status) => {
    const config = {
      'VERIFIED': { color: '#10b981', bg: '#d1fae5' },
      'PENDING': { color: '#f59e0b', bg: '#fef3c7' },
      'FAILED': { color: '#ef4444', bg: '#fee2e2' },
      'NOT_VERIFIED': { color: '#6b7280', bg: '#f3f4f6' }
    };

    const statusConfig = config[status] || config['NOT_VERIFIED'];
    return (
      <span 
        className="verification-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status || 'NOT_VERIFIED'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: phoneNumbers.length,
    active: phoneNumbers.filter(p => p.status === 'active').length,
    green: phoneNumbers.filter(p => p.phoneNumberQualityRating === 'GREEN').length,
    verified: phoneNumbers.filter(p => p.verificationStatus === 'VERIFIED').length
  };

  return (
    <div className="phone-numbers-container">
      <div className="phone-numbers-header">
        <div className="header-content">
          <h1>Phone Numbers</h1>
          <p>Manage your WhatsApp Business phone numbers and monitor quality ratings</p>
        </div>
       
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: '12px 16px', borderRadius: '6px', marginBottom: '20px' }}>
          Error: {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      <div className="phone-numbers-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Numbers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.green}</div>
          <div className="stat-label">High Quality</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.verified}</div>
          <div className="stat-label">Verified</div>
        </div>
      </div>

      <div className="phone-numbers-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by phone number, name, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="phone-numbers-table-container">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading phone numbers...
          </div>
        ) : (
          <table className="phone-numbers-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Display Name</th>
                <th>Quality Rating</th>
                <th>Verification Status</th>
                <th>Status</th>
               
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {phoneNumbers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state-cell">
                    <div className="empty-state">
                      <div className="empty-icon">📱</div>
                      <h3>No phone numbers found</h3>
                      <p>Add your first phone number to get started</p>
                      <button className="btn-add-first" onClick={handleAddPhoneNumber}>
                        Add Phone Number
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                phoneNumbers.map(phone => (
                  <tr key={phone.id} className={phone.phoneNumberQualityRating === 'RED' ? 'low-quality-row' : ''}>
                    <td>
                      <div className="phone-number-cell">
                        <strong>{phone.display_phone_number}</strong>
                        <span className="phone-id">ID: {phone.id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="verified-name-cell">
                        {phone.verified_name || 'Not Set'}
                      </div>
                    </td>
                    <td>{getQualityBadge(phone.quality_rating)}</td>
                    <td>{getVerificationStatusBadge(phone.name_status)}</td>
                    <td>{getStatusBadge(phone.status)}</td>
                   
                    <td>
                      <div className="phone-actions">
                       {phone.status !== 'CONNECTED' && (
                        <button
                          className="action-btn edit-btn"
                          onClick={() => openRegisterModal(phone)}
                          title="Connect"
                        >
                          Connect Number
                        </button>)}
                        
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <PhoneNumberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPhoneNumber(null);
        }}
        onSave={handleSavePhoneNumber}
        phoneNumber={editingPhoneNumber}
        loading={loading}
      />

      {isRegisterModalOpen && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-card" style={{ background: '#fff', width: '420px', maxWidth: '90vw', borderRadius: '12px', padding: '24px', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>Register Phone Number</h3>
              <button
                type="button"
                onClick={() => {
                  setIsRegisterModalOpen(false);
                  setSelectedPhoneForRegistration(null);
                  setRegistrationPin('');
                }}
                style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#475569' }}
              >
                ×
              </button>
            </div>

            <p style={{ margin: '0 0 16px', color: '#475569' }}>
              Enter the 6-digit PIN for <strong>{selectedPhoneForRegistration?.display_phone_number || selectedPhoneForRegistration?.displayPhoneNumber || selectedPhoneForRegistration?.id}</strong>
            </p>

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>PIN</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={registrationPin}
              onChange={(e) => setRegistrationPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '16px', marginBottom: '16px' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  setIsRegisterModalOpen(false);
                  setSelectedPhoneForRegistration(null);
                  setRegistrationPin('');
                }}
                style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConnectPhoneNumber}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}
              >
                Register
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPhoneNumberToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={phoneNumberToDelete?.displayPhoneNumber || ''}
        type="phone number"
      />
    </div>
  );
};

export default PhoneNumbers;

